import * as Long from "long";
import { configure, util } from "protobufjs";
import { QueueingSubject } from "queueing-subject";
import { BehaviorSubject, EMPTY, of, Subject, throwError as observableThrowError, } from "rxjs";
import { delay, filter, map, retryWhen, switchMap, tap } from "rxjs/operators";
import { errorNetworkIn, logNetworkIn, logNetworkOut, warnNetworkOut } from "../logging/logging";
import { utilHash } from "../utilHash";
import { rxWebSocket } from "./rxWebSocket";
var UtilNetwork = /** @class */ (function () {
    function UtilNetwork(id, proto, CommandCode, StatusCode, encodeWrapper, decodeWrapper, typeProto) {
        if (typeProto === void 0) { typeProto = proto; }
        var _this = this;
        this.id = id;
        this.proto = proto;
        this.CommandCode = CommandCode;
        this.StatusCode = StatusCode;
        this.encodeWrapper = encodeWrapper;
        this.decodeWrapper = decodeWrapper;
        this.typeProto = typeProto;
        this.connectionStatus$ = new BehaviorSubject({
            id: "None",
            status: 0,
        });
        this.netReceive$ = new Subject();
        this.sendingSubject = new QueueingSubject();
        this.responseStreamByCommandId = new Map();
        this.responseStreamTimeout = 3000;
        this.currentSequenceNumber = 0;
        this.areConnectionsStopped = false;
        this.disableConnections = function () {
            _this.areConnectionsStopped = true;
            logNetworkOut(_this.id + " \u21E2 Connections disabled");
        };
        this.enableConnections = function () {
            _this.areConnectionsStopped = false;
            logNetworkOut(_this.id + " \u21E2 Connections enabled");
        };
        this.connect = function (url) {
            _this.disconnect();
            _this.rxSocket = rxWebSocket(url, _this.sendingSubject);
            _this.connectionStatusSub$ = _this.rxSocket.connectionStatus$.subscribe(_this.handleConnectionStatus);
            _this.netReceiveSub$ = _this.rxSocket.receive$
                .pipe(retryWhen(function (errors) { return errors.pipe(delay(3000)); }), map(function (value) {
                return _this.decodeWrapper(value, _this.TypeResponse, _this.decodeCommand);
            }))
                .subscribe(_this.handleCommand);
        };
        this.disconnect = function () {
            if (!_this.rxSocket) {
                return;
            }
            _this.connectionStatusSub$.unsubscribe();
            _this.netReceiveSub$.unsubscribe();
        };
        this.netSend = function (command, allowMultiple) {
            if (allowMultiple === void 0) { allowMultiple = false; }
            var commandHash = utilHash.md5Hash(command);
            // allowMultiple was added (7/25/2019) in order to allow spammable commands
            // E.G: buy 1x corn 2-3 times
            // --------------------------
            // this is necessary not to send same command before response
            if (!allowMultiple && _this.responseStreamByCommandId.has(commandHash)) {
                _this.responseStreamClearTimeout = setTimeout(function () {
                    _this.responseStreamByCommandId.delete(commandHash);
                }, _this.responseStreamTimeout);
                return _this.responseStreamByCommandId.get(commandHash);
            }
            var sequenceNumber = ++_this.currentSequenceNumber;
            var encoded = _this.encode(sequenceNumber, command);
            if (encoded == undefined || _this.areConnectionsStopped) {
                return EMPTY;
            }
            _this.sendingSubject.next(encoded.buffer);
            logNetworkOut(_this.id + " -> [" + _this.CommandCode[command.id] + "#" + sequenceNumber + "]", command.props);
            var response$ = _this.netReceive$.pipe(filter(function (res) {
                return (res.id === command.id &&
                    res.sequenceNumber <= sequenceNumber &&
                    res.sequenceNumber > 0);
            }), tap(function (res) {
                _this.responseStreamByCommandId.delete(commandHash);
                clearTimeout(_this.responseStreamClearTimeout);
                logNetworkIn(_this.id + " <-\uFE0E <" + _this.StatusCode[res.statusCode] + "> [" + _this.CommandCode[command.id] + "#" + sequenceNumber + "]", res.props);
            }), switchMap(function (res) {
                return res.statusCode !== _this.StatusCode.OK
                    ? observableThrowError(new Error(_this.StatusCode[res.statusCode]))
                    : of(res.props);
            }));
            _this.responseStreamByCommandId.set(commandHash, response$);
            return response$;
        };
        this.handleCommand = function (value) {
            _this.netReceive$.next(value);
        };
        this.handleConnectionStatus = function (status) {
            _this.connectionStatus$.next({
                id: _this.id,
                status: status,
            });
        };
        this.encodeCommand = function (command) {
            var protoType = _this.proto.lookupType(_this.CommandCode[command.id] + "Request");
            var protoMessage = protoType.create(command.props);
            try {
                return protoType.encode(protoMessage).finish();
            }
            catch (e) {
                warnNetworkOut(_this.id + ": Couldn't encode message", _this.CommandCode[command.id], command.props);
            }
            return;
        };
        this.encode = function (sequenceNumber, command) {
            var encodedMessage = _this.encodeCommand(command);
            if (encodedMessage == undefined) {
                return;
            }
            return _this.encodeWrapper(command.id, _this.TypeRequest, {
                data: encodedMessage,
                sequenceNumber: sequenceNumber,
            });
        };
        this.decodeCommand = function (commandId, wrapper) {
            var decodedMessage;
            try {
                if (wrapper != undefined && wrapper.data != undefined) {
                    decodedMessage = _this.proto
                        .lookupType(_this.CommandCode[commandId] + "Response")
                        .decode(wrapper.data);
                    var sequenceNumber = wrapper.sequenceNumber || -1;
                    var statusCode = wrapper.statusCode || 0;
                    return {
                        id: commandId,
                        props: decodedMessage,
                        sequenceNumber: sequenceNumber,
                        statusCode: statusCode,
                    };
                }
            }
            catch (err) {
                // TODO: handle errors while decoding message
                errorNetworkIn("Error when decoding the message:", err);
            }
            return {
                id: commandId,
                props: undefined,
                sequenceNumber: -1,
                statusCode: _this.StatusCode.INVALID_ACTION,
            };
        };
        // Setup protobuf to use Long class
        util.Long = Long;
        configure();
        this.TypeRequest = this.typeProto.lookupType("Request");
        this.TypeResponse = this.typeProto.lookupType("Response");
    }
    return UtilNetwork;
}());
export { UtilNetwork };



//////////////////
// WEBPACK FOOTER
// ./src/utils/network/UtilNetwork.ts
// module id = 2991
// module chunks = 3